import { useRef, useState, useCallback, type MouseEvent } from 'react';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { gutters } from 'app/spacing';
import { WeekCalendarCell } from 'components/Calendar';
import useEndsOfScroll from 'hooks/useEndsOfScroll';
import { ActivityPill } from './ActivityPill';
import { ScheduleActivityButton } from './ScheduleActivityButton';
import { CALENDAR_VIEW_OPTIONS } from 'components/Calendar/constants';
import ScrollFadeContainer from '__components/Kizen/ScrollFadeContainer';
import type { CalendarItem, ScheduledActivity } from '../types';
import { ExternalCalendarEventPill } from './common';

const StyledWeekCell = styled(WeekCalendarCell)<{ skipped?: boolean }>`
  position: relative;
  padding-bottom: ${gutters.spacing(0, 3)}px;
  padding-top: ${gutters.spacing()}px;
  ${({ skipped }) =>
    skipped
      ? css`
          background-image: repeating-linear-gradient(
            -35deg,
            transparent 10px,
            var(--color-background-medium) 11px,
            transparent 12px,
            transparent 20px
          );
        `
      : ''}
`;

const EventsScrollContainerWrapper = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
`;

const EventsScrollContainer = styled(ScrollFadeContainer)`
  overflow: overlay;
  overflow: auto;
  height: 100%;
  width: 100%;
  padding-left: ${gutters.spacing()}px;
  padding-right: ${gutters.spacing(0, 3)}px;

  > div {
    width: 100%;
  }
`;

interface BuildWeekCellProps {
  activitiesLookup: Record<string, (ScheduledActivity | CalendarItem)[]>;
  onActivityClick: (activity: ScheduledActivity) => void;
  onActivityAction: (activity: ScheduledActivity, action: string) => void;
  onAddActivity: (date: Date, time: string) => void;
  canEdit: boolean;
  timezone: string;
  businessTimezone: string;
  skipLookup?: Record<string, boolean>;
  skipWeekdays?: number[];
  isMobile?: boolean;
}

export const buildWeekCell =
  ({
    activitiesLookup,
    onActivityClick,
    onActivityAction,
    onAddActivity,
    canEdit,
    timezone,
    businessTimezone,
    skipLookup = {},
    skipWeekdays = [],
    isMobile,
  }: BuildWeekCellProps) =>
  ({ date, time, columnIndex, totalColumns, ...rest }: any) => {
    const [showAddButton, setShowAddButton] = useState(false);

    // Create lookup key from date and time
    const dateKey = `${date.toDateString()}-${time}`;
    const activities = activitiesLookup[dateKey] || [];

    // Determine if this date should be marked as skipped (non-working day)
    const skipped = skipLookup[date.toDateString()];
    const skippedByWeekday = skipWeekdays.includes(date.getDay());
    // Show overlay if: explicitly skipped OR (not explicitly unskipped AND is a skip weekday)
    const isSkipped =
      skipped === true || (skipped !== false && skippedByWeekday);

    const handleAddClick = useCallback(
      (e: MouseEvent) => {
        e.stopPropagation();
        onAddActivity(date, time);
      },
      [date, time]
    );

    const handleMouseEnter = useCallback(() => {
      setShowAddButton(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
      setShowAddButton(false);
    }, []);

    const scrollRef = useRef<HTMLDivElement>(null);
    const scrolled = useEndsOfScroll(scrollRef, [activities]);

    return (
      <StyledWeekCell
        date={date}
        skipped={isSkipped}
        {...rest}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {canEdit ? (
          <ScheduleActivityButton
            view={CALENDAR_VIEW_OPTIONS.week}
            show={showAddButton}
            onClick={handleAddClick}
          />
        ) : null}
        <EventsScrollContainerWrapper>
          <EventsScrollContainer
            className="kds-expanding-scrollbar"
            ref={scrollRef}
            scrolled={scrolled}
          >
            <div>
              {activities.map((activity) => {
                if (activity.isCalendarEvent) {
                  return (
                    <ExternalCalendarEventPill
                      key={activity.id}
                      id={activity.id}
                      date={activity.date}
                      isSpanning={activity.isSpanning}
                      allDay={activity.allDay}
                      disableHoverEffect
                      customColor="var(--color-viz-cat-1-primary)"
                      isPastDate={
                        (activity.calculationDate ?? activity.dueDatetime) <
                        Date.now()
                      }
                      url={activity.url}
                      columnIndex={columnIndex}
                      totalColumns={totalColumns}
                      event={activity}
                    >
                      {activity.name}
                    </ExternalCalendarEventPill>
                  );
                }
                return (
                  <ActivityPill
                    key={activity.id}
                    activity={activity}
                    timezone={timezone}
                    businessTimezone={businessTimezone}
                    onComplete={onActivityClick}
                    onEdit={(act) => onActivityAction(act, 'edit')}
                    onDelete={(id) =>
                      onActivityAction(
                        activities.find(
                          (a) => a.id === id
                        ) as ScheduledActivity,
                        'delete'
                      )
                    }
                    canEdit={canEdit}
                    columnIndex={columnIndex}
                    totalColumns={totalColumns}
                    isMobile={isMobile}
                    // Disable the hover effect on the pill since we do it with Javascript in order to highlight all pills with the same activity id across cells
                    disableHoverEffect
                  />
                );
              })}
            </div>
          </EventsScrollContainer>
        </EventsScrollContainerWrapper>
      </StyledWeekCell>
    );
  };
